import React, { FC, useCallback, useMemo } from 'react';
import UploadWithSampleModal from '../flatfile/UploadWithSampleModal';
import {
	getFlatfileRecordForPart,
	PART_MASTER_FLATFILE_HEADER,
	PARTIAL_UPLOAD_CRUD_FIELD,
	PARTS_MASTER_LIST_PARTIAL_FLATFILE_HEADER,
} from '../stock_locations_detail_details_page/stock_location_constants';
import { getCurrency } from '../../utils/DataAccessUtils';
import { CSV_EXPORT_DEFAULTS } from '../../utils/DataConstants';
import { connect } from 'react-redux';
import { ExportToCsv } from 'export-to-csv';
import { requiredWithValueRecordHook } from '../flatfile/faltfile_utils';
import { isGLCodeAllowedBySettings } from '../../utils/InventoryUtils';
import { TagOption } from '../../types/tags';

const PARTIAL_SAMPLE_TAGS = ['Example Tag 1', 'Example Tag 2'];

const PartialPartsUpload: FC<any> = ({
	hideUploadPopup,
	vendorOptions,
	onDataUpload,
	currentUser,
	companyConfig,
	tagOptions,
}): React.ReactElement => {
	const allTagOptions: TagOption[] = useMemo(() => tagOptions || [], [tagOptions]);

	const userCurrency = useMemo(() => getCurrency({ currentUser }).id, [currentUser]);

	const glCodeEnabled = useMemo(() => isGLCodeAllowedBySettings(companyConfig), [companyConfig]);

	const headerDefs = useMemo(
		() => [
			PARTIAL_UPLOAD_CRUD_FIELD,
			...PART_MASTER_FLATFILE_HEADER([userCurrency], vendorOptions, glCodeEnabled, allTagOptions),
		],
		[userCurrency, vendorOptions, glCodeEnabled, allTagOptions]
	);

	const onDownloadSample = useCallback(() => {
		const csvTitle = `Partial_Parts_Upload_Sample`;

		const csvExporter = new ExportToCsv({
			...CSV_EXPORT_DEFAULTS,
			filename: csvTitle,
			title: csvTitle,
			headers: headerDefs.map((h) => h.label),
		});

		const data = [
			{
				...getFlatfileRecordForPart({ tags: PARTIAL_SAMPLE_TAGS }, userCurrency, headerDefs),
				crudAction: 'create',
			},
			{
				...getFlatfileRecordForPart(
					{ id: 1234, tags: PARTIAL_SAMPLE_TAGS },
					userCurrency,
					headerDefs
				),
				crudAction: 'update',
			},
			{
				...getFlatfileRecordForPart(
					{ id: 1234, tags: PARTIAL_SAMPLE_TAGS },
					userCurrency,
					headerDefs
				),
				crudAction: 'delete',
			},
		];
		csvExporter.generateCsv(data);
	}, [headerDefs, userCurrency]);

	const recordHook = useCallback(
		(record) => {
			let result = requiredWithValueRecordHook(['id'], 'crudAction', ['update', 'delete'])(record);

			const tagsValue = result.get('tags');
			if (tagsValue) {
				// Flatfile may keep the CSV comma-separated string as one value for enum-list.
				// Split it, map each label to the matching option value.
				let rawTags: string[] = [];
				if (typeof tagsValue === 'string' && tagsValue.trim()) {
					rawTags = tagsValue
						.split(',')
						.map((s) => s.trim())
						.filter(Boolean);
				} else if (Array.isArray(tagsValue)) {
					rawTags = tagsValue
						.flatMap((v) => String(v).split(','))
						.map((s) => s.trim())
						.filter(Boolean);
				}

				if (rawTags.length > 0) {
					const validValues: string[] = [];
					const invalidLabels: string[] = [];
					rawTags.forEach((labelOrId) => {
						const match = allTagOptions.find(
							(opt) => opt.label === labelOrId || opt.value === labelOrId
						);
						if (match) {
							validValues.push(match.value);
						} else {
							invalidLabels.push(labelOrId);
						}
					});

					result.set('tags', validValues);
					if (invalidLabels.length > 0) {
						result.addError(
							'tags',
							`Unknown tags: ${invalidLabels.join(', ')}. Please select from existing tags only.`
						);
					}
				}
			}

			return result;
		},
		[allTagOptions]
	);

	return (
		<UploadWithSampleModal
			type="Partial Parts Upload"
			title="Partial Parts Upload"
			downloadText="Do you want to download Sample?"
			sampleFileText="Download Sample"
			recordHook={recordHook}
			onCancel={hideUploadPopup}
			onDownloadSample={onDownloadSample}
			fields={PARTS_MASTER_LIST_PARTIAL_FLATFILE_HEADER(
				[userCurrency],
				vendorOptions,
				glCodeEnabled,
				allTagOptions
			)}
			onDataLoad={onDataUpload}
		/>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	companyConfig: state.company_config.detail,
});

export default connect(mapStateToProps)(PartialPartsUpload as any);
